import { Typography, Button, IconButton, Tooltip, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { BaseTable } from "components";
import React, { useMemo } from "react";
import type { PlantUserRow } from "../../types";
import { UseTheme } from "lib/hooks";
import * as table from "lib/table";
import { TableHeaderStyles } from "lib/styles/table";
import { GradientHeaderText } from "lib/styles/header";

export interface PlantUsersTableProps {
	rows: PlantUserRow[];
	order: import("enums/sort").SortEnum;
	onRequestSort: (key: keyof PlantUserRow) => void;
	loading?: boolean;
	page?: number; // Page number (0-indexed)
	rowsPerPage?: number; // Rows per page
	title?: string; // Table title
	onAddClick?: () => void;
	onEditClick?: (gmin: string) => void;
	onDeleteSuccess?: () => void; // Callback after successful delete
}

/**
 * Table component for displaying Plant Users.
 *
 * Displays one row per Plant User per sector (users with multiple sectors appear in multiple rows).
 * Columns: Name, Email, Sector, Plants (comma-separated list).
 *
 * Follows the same styling and structure pattern as DashboardTable.tsx.
 *
 * @param props - Component props
 * @param props.rows - Array of PlantUserRow to display
 * @param props.order - SortEnum for current sort state
 * @param props.onRequestSort - Callback when user clicks column header to sort
 * @param props.loading - Whether data is currently loading
 * @param props.page - Current page number (0-indexed)
 * @param props.rowsPerPage - Number of rows per page
 * @param props.title - Table title (defaults to "Plant Users")
 */
export function PlantUsersTable({
	rows,
	order,
	onRequestSort,
	loading = false,
	page = 0,
	rowsPerPage = 25,
	title = "Plant Users",
	onAddClick,
	onEditClick,
	onDeleteSuccess,
}: PlantUsersTableProps) {
	const { theme } = UseTheme();

	// Build table column definitions using shared table helpers
	const headCells = useMemo(
		() =>
			table.CreateHeadCells<
				PlantUserRow,
				unknown,
				keyof PlantUserRow,
				string
			>(
				[
					{ id: "name", numeric: false, label: "Name" },
					{
						id: "email",
						numeric: false,
						label: "Email",
						mapper: (o) => o || "",
					},
					{ id: "sector", numeric: false, label: "Sector" },
					{
						id: "plants",
						numeric: false,
						label: "Plants",
						mapper: (o) =>
							Array.isArray(o) && o.length > 0
								? o.join(", ")
								: "—",
					},
				],
				(property) => {
					// Map headCell ID to row property for sorting
					const fieldMap: Record<string, keyof PlantUserRow> = {
						name: "name",
						email: "email",
						sector: "sector",
						plants: "plants",
					};
					const field = fieldMap[property as string];
					if (field) {
						onRequestSort(field);
					}
				}
			),
		[onRequestSort]
	);

	const handleDeleteClick = (row: PlantUserRow) => {
		// Route delete to edit dialog - the edit dialog will handle delete confirmation
		if (onEditClick) {
			onEditClick(row.userId);
		}
	};

	return (
		<>
			<BaseTable<PlantUserRow, keyof PlantUserRow>
				data={rows}
				headCells={headCells}
				order={order}
				page={page}
				rowsPerPage={rowsPerPage}
				config={{
					stickyHeader: true,
					header: {
						title: (
							<Typography
								variant="h5"
								sx={GradientHeaderText(theme, false)}
							>
								{title}
							</Typography>
						),
						actions: [
							<Button
								key="add-plant-user"
								variant="text"
								color="primary"
								size="small"
								sx={{ textTransform: "none" }}
								onClick={onAddClick}
								disabled={loading || !onAddClick}
							>
								+ Add Plant User
							</Button>,
						],
					},
				}}
				loading={loading}
				hideActions={false}
				renderActions={(row) => {
					return (
						<Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
							{onEditClick && (
								<Tooltip title="Edit Plant User">
									<IconButton
										onClick={(e) => {
											e.stopPropagation(); // Prevent row click
											onEditClick(row.userId);
										}}
										color="primary"
										size="small"
									>
										<EditIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}							
						</Box>
					);
				}}
				getRowId={(row) => `${row.userId}-${row.sectorId}`}
				sx={TableHeaderStyles(theme)}
			/>
		</>
	);
}
